import { ROUTE_BUILDER } from '@/constants/pages';
import { getGeoLocation, isServer } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { showGeolocationErrorToast } from '@/helpers/toast';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
} from '@/hooks/search/useSearch.constants';
import {
  buildUrlSearchParams,
  getKeywordWithSlug,
  getLocationWithSlug,
  getSearchUrlParameters,
  isDealsSearch,
  positionSchema,
  SearchType,
} from '@/hooks/search/useSearch.helpers';
import { Tracking } from '@/types/analytics';
import { MouseEvent } from 'react';
import { isSmtOnlySearchEnabled } from '../deals/hooks/useSmtOnlyToggle';
import {
  getLastLocationSuggestionFromLocalStorage,
  hasNoDefaultLocation,
} from '../searchV2/components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.helpers';
import { SearchContext } from '../searchV2/types/tracking';
import { TabIdentifier } from './MegaMenu.types';
import { baseTranslationKey } from './modules/Category';
import { MegaMenuSubCategory } from './modules/Category.types';

export function handleOnCategoryLinkClick(
  e: MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>,
  to: string,
  history: any /** no types for history api with out react-rouder-dom version */,
  context: SearchContext,
  cb?: () => void,
) {
  /**
   * If user clicks a link with the "near me" location we need to fetch the user's geolocation
   * and then navigate to the search page with the user's location.
   */
  if (to.includes(encodeURIComponent(DEFAULT_MY_LOCATION_KEYWORD))) {
    e.preventDefault();
    /**
     * isFetchingUserPosition check in location state is done inside location search form input to display the loading spinner
     * if user's geolocation is being fetched.
     */
    const url: {
      pathname: string;
      search?: string;
      state: { isFetchingUserPosition: boolean; searchContext: SearchContext };
    } = {
      pathname: to,
      state: { isFetchingUserPosition: true, searchContext: context },
    };

    const goto = (location: { pathname: string; search?: string; state?: { isFetchingUserPosition: boolean } }) => {
      /**
       * Timeout to ensure loading state in location input shows
       */
      location['state']['searchContext'] = context;
      setTimeout(() => history.replace(location), 1);
    };

    getGeoLocation(
      (lat: string, lon: string) => {
        goto({ ...url, search: `?lat=${lat}&lon=${lon}`, state: { isFetchingUserPosition: false } });
      },
      () => {},
      () => {
        /**
         * on loading geo location
         */
        goto({ pathname: to, state: { isFetchingUserPosition: true } });
      },
      (error: GeolocationPositionError) => {
        const deniedKey = `${baseTranslationKey}.snackbar.error.userLocationDenied` as const;
        showGeolocationErrorToast(error, { deniedKey, autoClose: false });
        goto({ pathname: to, state: { isFetchingUserPosition: false } });
      },
    );
  }

  cb?.();
}

/**
 * Builds the link and keeps current search location if it is not the default one
 * and keeps the search parameters from the current search for a better user experience.
 *
 */
export function buildClientCategoryLink(
  to: string,
  match: any /** no types for routeMatch api with out react-rouder-dom version */,
  context: SearchContext,
  keepPosition?: boolean,
  locationSlug?: string,
) {
  const currentLocation = locationSlug ?? DEFAULT_SEARCH_LOCATION;
  const currentLocationNotDefault = currentLocation && currentLocation !== DEFAULT_SEARCH_LOCATION;
  const urlState = getSearchUrlParameters({ match, fromMegaMenu: true });
  let [pathname, searchQuery] = to.split('?');
  let search;
  let isMyLocation = to.includes(DEFAULT_MY_LOCATION_KEYWORD);

  if (currentLocationNotDefault) {
    if (to.includes(DEFAULT_SEARCH_LOCATION)) {
      pathname = pathname.replace(DEFAULT_SEARCH_LOCATION, currentLocation);
      isMyLocation = currentLocation === DEFAULT_MY_LOCATION_KEYWORD;
    }
  }

  const position = `${urlState['lat']}-${urlState['lon']}`;
  isMyLocation = isMyLocation && positionSchema.safeParse(position).success;

  if (searchQuery) {
    search = new URLSearchParams(searchQuery);

    /**
     * if the to link includes a search query (like ?gc=1 etc)
     * we reset the search query to that but we still need
     * to keep the lat & lon if the user is searching for "Nära mig" (currentLocation)
     */
    if (isMyLocation) {
      search.set('lat', urlState['lat']);
      search.set('lon', urlState['lon']);
    }
  } else {
    let _urlState = { ...urlState };
    if (!isMyLocation || keepPosition === false) {
      delete _urlState.lat;
      delete _urlState.lon;
    }
    /**
     * dont keep q and location as query params in mega menu links/tabs
     */
    delete _urlState.q;
    delete _urlState.location;
    /**
     * dont keep prefs in mega menu links/tabs
     */
    delete _urlState.prefs;

    /**
     * delete sort
     */
    delete _urlState.sort;

    /**
     *  remove page from url state
     */
    if (_urlState?.page) {
      delete _urlState.page;
    }

    search = buildUrlSearchParams(_urlState, _urlState, true);
  }

  return { pathname, search: search.toString(), state: { searchContext: context } };
}

export function trackMegaMenuClick(
  tab: TabIdentifier,
  cta: MegaMenuSubCategory['links'][number] | null,
  screenName: Tracking.ScreenName,
) {
  trackMpEvent({
    name: 'mega_menu_click',
    properties: {
      screen_name: screenName,
      mega_menu_tab: tab,
      mega_menu_cta: cta?.label?.replace?.(/ /g, '_'),
    },
  });
}

export function buildSerpOrSokLink(
  keyword: string = DEFAULT_SEARCH_KEYWORD,
  location: string = DEFAULT_SEARCH_LOCATION,
  searchType: SearchType = 'default',
  searchParamsString?: string | undefined,
  redirectedFromPreview = false,
): { pathname: string; searchString: string | undefined } {
  const indexableKeyword = getKeywordWithSlug(keyword);
  const indexableLocation = getLocationWithSlug(location);

  let pathname;

  let searchParams = new URLSearchParams(searchParamsString);

  if (indexableKeyword?.slug && indexableLocation?.slug) {
    if (searchType === 'sistaminuten') {
      pathname = ROUTE_BUILDER.sistaMinutenSerp(indexableKeyword?.slug, indexableLocation?.slug);
    } else if (isDealsSearch(searchType)) {
      if (isSmtOnlySearchEnabled(searchType, redirectedFromPreview)) {
        pathname = ROUTE_BUILDER.sistaMinutenSerp(indexableKeyword?.slug, indexableLocation?.slug);
      } else {
        pathname = ROUTE_BUILDER.dealsSerpPreview(indexableKeyword?.slug, indexableLocation?.slug);
      }
    } else {
      pathname = ROUTE_BUILDER.serp(indexableKeyword?.slug, indexableLocation?.slug);
    }

    searchParams.delete('q');
    searchParams.delete('location');
  } else {
    if (searchType === 'sistaminuten') {
      pathname = ROUTE_BUILDER.sistaMinutenSerpNoIndex();
    } else if (isDealsSearch(searchType)) {
      if (isSmtOnlySearchEnabled(searchType, redirectedFromPreview)) {
        pathname = ROUTE_BUILDER.sistaMinutenSerpNoIndex();
      } else {
        pathname = ROUTE_BUILDER.dealsSerpPreviewNoIndex();
      }
    } else {
      pathname = ROUTE_BUILDER.serpNoIndex();
    }

    searchParams.set('q', keyword);
    searchParams.set('location', location);
  }

  return { pathname, searchString: searchParams.toString() };
}

export function buildSistaminutenLink(keyword = DEFAULT_SEARCH_KEYWORD, location) {
  if (!isServer && !location) {
    if (!hasNoDefaultLocation()) {
      location = getLastLocationSuggestionFromLocalStorage()?.label || DEFAULT_SEARCH_LOCATION;
    }
  }

  return ROUTE_BUILDER.sistaMinutenSerp(keyword, location || DEFAULT_SEARCH_LOCATION);
}

export function buildDealsSerpLink(keyword = DEFAULT_SEARCH_KEYWORD, location) {
  if (!isServer && !location) {
    if (!hasNoDefaultLocation()) {
      location = getLastLocationSuggestionFromLocalStorage()?.label || DEFAULT_SEARCH_LOCATION;
    }
  }

  return ROUTE_BUILDER.dealsSerpPreview(keyword, location || DEFAULT_SEARCH_LOCATION);
}
